import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import AdminLayout from '@/components/Layout';
import { Users, Radio, Tv, Package, BarChart3, TrendingUp, UserPlus, Activity, LineChart as LineChartIcon, DollarSign, Repeat, Wallet, UserMinus, HeartCrack } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line } from 'recharts';

// Aura Gold + komplementer renkler (slate temayla uyumlu)
const COLORS = {
  gold:      '#F5A623',
  goldDeep:  '#D97706',
  green:     '#10B981',
  red:       '#EF4444',
  gray:      '#64748B',
  goldFaint: 'rgba(245, 166, 35, 0.12)',
};

const STATUS_COLORS = {
  active:    COLORS.green,
  suspended: COLORS.gold,
  cancelled: COLORS.red,
};

export default function Analytics() {
  const [stats, setStats] = useState(null);
  const [fin, setFin] = useState(null);
  const [cancellations, setCancellations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [s, f, c] = await Promise.all([
        api.get('/analytics/stats'),
        api.get('/analytics/financials'),
        api.get('/analytics/cancellations'),
      ]);
      setStats(s.data);
      setFin(f.data);
      setCancellations(c.data);
    } catch (err) {
      console.error('Analytics yüklenemedi:', err);
      setError('İstatistikler yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-6 text-gray-500 italic">Analitik veriler yükleniyor…</div>
      </AdminLayout>
    );
  }

  if (error || !stats) {
    return (
      <AdminLayout>
        <div className="p-6 text-red-400">{error || 'Veri yok.'}</div>
      </AdminLayout>
    );
  }

  const k = stats.kpis;
  const pieData = Object.entries(stats.customers_by_status)
    .filter(([_, v]) => v > 0)
    .map(([key, value]) => ({
      name: key === 'active' ? 'Aktif' : key === 'suspended' ? 'Askıda' : 'İptal',
      value,
      color: STATUS_COLORS[key],
    }));

  const totalCustomers = stats.totals.customers;
  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      const diffMs = Date.now() - d.getTime();
      const diffMin = Math.floor(diffMs / 60000);
      if (diffMin < 1) return 'Az önce';
      if (diffMin < 60) return `${diffMin}dk önce`;
      const diffHr = Math.floor(diffMin / 60);
      if (diffHr < 24) return `${diffHr}sa önce`;
      const diffDay = Math.floor(diffHr / 24);
      if (diffDay < 30) return `${diffDay}g önce`;
      return d.toLocaleDateString('tr-TR', { day: '2-digit', month: 'short' });
    } catch { return '—'; }
  };

  return (
    <AdminLayout>
      <div className="p-6 space-y-6">
        <div className="flex justify-between items-end mb-2">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3" data-testid="analytics-page-title">
              <BarChart3 className="text-amber-500" /> Analitik & Sistem Nabzı
            </h1>
            <p className="text-xs text-gray-500 mt-1 ml-9">İşletmenin canlı görünümü — son güncellenen veriler.</p>
          </div>
          <button
            onClick={load}
            className="text-xs font-bold uppercase tracking-wider text-amber-500 hover:text-amber-400 px-3 py-2 rounded-lg hover:bg-amber-500/5 transition"
            data-testid="analytics-refresh-btn"
          >
            <Activity size={14} className="inline mr-1" />
            Yenile
          </button>
        </div>

        {/* Financial KPIs (Aura Gold parıltılı) */}
        {fin && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="analytics-financials">
            <FinancialCard
              icon={Repeat}
              label="MRR (Aylık Tekrarlayan)"
              value={`€${fin.mrr.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`}
              sub={`${fin.tx_count_30d} işlem · son 30 gün`}
              testId="fin-mrr"
            />
            <FinancialCard
              icon={TrendingUp}
              label="ARR (Yıllık Tahmin)"
              value={`€${fin.arr.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`}
              sub={`MRR × 12`}
              testId="fin-arr"
            />
            <FinancialCard
              icon={DollarSign}
              label="Toplam Gelir"
              value={`€${fin.total_revenue.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`}
              sub={`${fin.active_subscriptions} aktif abone · ARPU €${fin.arpu}`}
              testId="fin-total"
            />
            <FinancialCard
              icon={UserMinus}
              label="Churn Oranı"
              value={`%${fin.churn_rate.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`}
              sub={`${fin.cancelled_customers} iptal eden müşteri`}
              tone={fin.churn_rate > 10 ? 'red' : 'gold'}
              testId="fin-churn"
            />
          </div>
        )}

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="analytics-kpis">
          <KpiCard
            icon={Users}
            label="Toplam Müşteri"
            value={k.customers_total}
            sub={`${stats.customers_by_status.active} aktif`}
            tone="gold"
            testId="kpi-customers"
          />
          <KpiCard
            icon={Radio}
            label="Aktif Hatlar"
            value={k.lines_active}
            sub={`${stats.totals.lines} toplam`}
            tone="green"
            testId="kpi-lines"
          />
          <KpiCard
            icon={Tv}
            label="Toplam Yayın"
            value={k.channels_total}
            sub={`${stats.totals.movies + stats.totals.series} VOD eser`}
            tone="gold"
            testId="kpi-channels"
          />
          <KpiCard
            icon={Package}
            label="Toplam Paket"
            value={k.bouquets_total}
            sub={`${stats.totals.categories} kategori`}
            tone="amber-deep"
            testId="kpi-bouquets"
          />
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Pie Chart - Customer status */}
          <Panel title="Müşteri Durumu" icon={Users} testId="analytics-status-pie">
            {totalCustomers === 0 ? (
              <EmptyChart message="Henüz müşteri yok." />
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={85}
                      paddingAngle={3}
                      dataKey="value"
                      stroke="#0F172A"
                      strokeWidth={2}
                    >
                      {pieData.map((entry, idx) => (
                        <Cell key={idx} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={tooltipStyle} cursor={false} />
                    <Legend wrapperStyle={{ fontSize: '11px', color: '#94A3B8' }} iconType="circle" />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
            <div className="mt-3 grid grid-cols-3 gap-2 text-center">
              {pieData.map(d => (
                <div key={d.name} className="bg-slate-950 rounded-lg p-2 border border-slate-800">
                  <div className="text-lg font-bold" style={{ color: d.color }}>{d.value}</div>
                  <div className="text-[9px] uppercase tracking-wider text-gray-500 font-bold">{d.name}</div>
                </div>
              ))}
            </div>
          </Panel>

          {/* Top 5 Bouquets bar */}
          <Panel
            title="En Çok Tercih Edilen Paketler"
            icon={TrendingUp}
            className="lg:col-span-2"
            testId="analytics-top-bouquets"
          >
            {stats.top_bouquets.length === 0 ? (
              <EmptyChart message="Henüz paket atanmadı. /admin/bouquets sayfasından paket oluşturun ve müşterilere atayın." />
            ) : (
              <>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={stats.top_bouquets} layout="vertical" margin={{ left: 4, right: 16 }}>
                      <CartesianGrid stroke="#1E293B" strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" stroke="#475569" fontSize={11} allowDecimals={false} />
                      <YAxis
                        type="category"
                        dataKey="name"
                        stroke="#94A3B8"
                        fontSize={11}
                        width={140}
                        tick={{ fill: '#CBD5E1' }}
                      />
                      <Tooltip contentStyle={tooltipStyle} cursor={{ fill: COLORS.goldFaint }} />
                      <Bar dataKey="customer_count" fill={COLORS.gold} radius={[0, 6, 6, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-3 space-y-1.5" data-testid="top-bouquets-list">
                  {stats.top_bouquets.map((b, i) => (
                    <div key={b.id} className="flex items-center gap-3 text-xs px-2 py-1.5 hover:bg-slate-800/40 rounded">
                      <span className="w-5 h-5 rounded-full bg-amber-500/15 text-amber-400 flex items-center justify-center font-bold text-[10px] shrink-0">
                        {i + 1}
                      </span>
                      <span className="flex-1 text-white font-medium truncate">{b.name}</span>
                      <span className="font-mono text-gray-500 text-[10px]">{b.channel_count} kanal</span>
                      <span className="font-mono text-amber-400 font-bold">{b.customer_count}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </Panel>
        </div>

        {/* Time series — son 7 gün */}
        <Panel title="Son 7 Gün — Yeni Müşteri & Hat" icon={LineChartIcon} testId="analytics-timeseries">
          {(!stats.timeseries_7d || stats.timeseries_7d.length === 0) ? (
            <EmptyChart message="Zaman serisi verisi yok." />
          ) : (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={stats.timeseries_7d} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke="#1E293B" strokeDasharray="3 3" />
                  <XAxis dataKey="label" stroke="#475569" fontSize={11} />
                  <YAxis stroke="#475569" fontSize={11} allowDecimals={false} />
                  <Tooltip contentStyle={tooltipStyle} cursor={{ fill: COLORS.goldFaint }} />
                  <Legend wrapperStyle={{ fontSize: '11px', color: '#94A3B8' }} iconType="circle" />
                  <Line
                    type="monotone"
                    dataKey="customers"
                    name="Müşteri"
                    stroke={COLORS.gold}
                    strokeWidth={2.5}
                    dot={{ r: 4, fill: COLORS.gold, stroke: '#0F172A', strokeWidth: 2 }}
                    activeDot={{ r: 6 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="lines"
                    name="Hat"
                    stroke={COLORS.green}
                    strokeWidth={2.5}
                    dot={{ r: 4, fill: COLORS.green, stroke: '#0F172A', strokeWidth: 2 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </Panel>

        {/* Cancellation Reasons — retention analizi */}
        <Panel title="İptal Nedenleri (Retention Analizi)" icon={HeartCrack} testId="analytics-cancellations">
          {(!cancellations || cancellations.total_cancellations === 0) ? (
            <EmptyChart message="Henüz iptal eden müşteri yok 🎉" />
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={cancellations.breakdown.map(b => ({
                        name: b.label, value: b.count, percent: b.percent, reason: b.reason,
                      }))}
                      cx="50%" cy="50%" innerRadius={45} outerRadius={90} paddingAngle={3}
                      dataKey="value" stroke="#0F172A" strokeWidth={2}
                    >
                      {cancellations.breakdown.map((b, idx) => (
                        <Cell
                          key={idx}
                          fill={[COLORS.red, COLORS.gold, COLORS.goldDeep, '#A855F7', '#06B6D4', COLORS.gray][idx % 6]}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={tooltipStyle}
                      formatter={(v, _n, props) => [`${v} müşteri (%${props.payload.percent})`, props.payload.name]}
                    />
                    <Legend wrapperStyle={{ fontSize: '11px', color: '#94A3B8' }} iconType="circle" />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-2" data-testid="analytics-cancellations-list">
                <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-2">
                  Toplam {cancellations.total_cancellations} iptal — sıralı dağılım
                </div>
                {cancellations.breakdown.map((b, idx) => (
                  <div
                    key={b.reason}
                    className="flex items-center justify-between bg-slate-950 border border-slate-800 rounded-lg px-3 py-2"
                    data-testid={`analytics-cancellation-row-${b.reason}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span
                        className="w-2.5 h-2.5 rounded-full shrink-0"
                        style={{ background: [COLORS.red, COLORS.gold, COLORS.goldDeep, '#A855F7', '#06B6D4', COLORS.gray][idx % 6] }}
                      />
                      <span className="text-sm text-white truncate">{b.label}</span>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-[11px] font-mono text-gray-400">{b.count} müşteri</span>
                      <span className="text-xs font-mono font-bold text-amber-400 tabular-nums">%{b.percent.toFixed(1)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Panel>

        {/* Recent activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Panel title="Son Eklenen Müşteriler" icon={UserPlus} testId="analytics-recent-customers">
            {stats.recent_customers.length === 0 ? (
              <EmptyChart message="Henüz müşteri yok." />
            ) : (
              <div className="divide-y divide-slate-800/60">
                {stats.recent_customers.map(c => (
                  <div key={c.id} className="flex items-center gap-3 py-2.5" data-testid={`recent-customer-${c.id}`}>
                    <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-amber-500 font-bold text-sm shrink-0">
                      {(c.full_name || '—').charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white font-medium truncate">{c.full_name}</div>
                      <div className="text-[10px] font-mono text-gray-500 truncate">{c.email}</div>
                    </div>
                    <span className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded-full ${c.status === 'active' ? 'bg-green-500/10 text-green-500' : 'bg-gray-700 text-gray-400'}`}>
                      {c.status}
                    </span>
                    <span className="text-[10px] font-mono text-gray-600 w-16 text-right shrink-0">{formatDate(c.created_at)}</span>
                  </div>
                ))}
              </div>
            )}
          </Panel>

          <Panel title="Son Eklenen Hatlar" icon={Radio} testId="analytics-recent-lines">
            {stats.recent_lines.length === 0 ? (
              <EmptyChart message="Henüz hat yok." />
            ) : (
              <div className="divide-y divide-slate-800/60">
                {stats.recent_lines.map(l => (
                  <div key={l.id} className="flex items-center gap-3 py-2.5" data-testid={`recent-line-${l.id}`}>
                    <div className="w-8 h-8 rounded bg-slate-800 flex items-center justify-center text-amber-500 shrink-0">
                      <Radio size={14} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-amber-400 font-mono font-bold truncate">{l.username}</div>
                      <div className="text-[10px] font-mono text-gray-500 truncate">{l.package_name}</div>
                    </div>
                    <span className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded-full ${l.status === 'active' ? 'bg-green-500/10 text-green-500' : 'bg-gray-700 text-gray-400'}`}>
                      {l.status}
                    </span>
                    <span className="text-[10px] font-mono text-gray-600 w-16 text-right shrink-0">{formatDate(l.created_at)}</span>
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </div>
      </div>
    </AdminLayout>
  );
}

/* ---------- Sub-components ---------- */

function FinancialCard({ icon: Icon, label, value, sub, tone = 'gold', testId }) {
  // Tone palette — gold default, red for high churn
  const palette = tone === 'red'
    ? { bg: 'from-red-500/15', border: 'border-red-700/40', text: 'text-red-400', glow: 'shadow-red-500/15' }
    : { bg: 'from-amber-500/20', border: 'border-amber-500/40', text: 'text-amber-400', glow: 'shadow-amber-500/20' };
  return (
    <div
      className={`relative bg-slate-900 border ${palette.border} bg-gradient-to-br ${palette.bg} via-amber-500/[0.03] to-slate-900 rounded-xl p-5 overflow-hidden group hover:scale-[1.02] transition shadow-lg ${palette.glow}`}
      data-testid={testId}
    >
      {/* Aura glow */}
      {tone === 'gold' && (
        <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full bg-amber-500/10 blur-3xl pointer-events-none group-hover:bg-amber-500/15 transition" />
      )}
      <div className={`relative flex items-center gap-2 ${palette.text} mb-3`}>
        <Icon size={16} />
        <span className="text-[10px] font-bold uppercase tracking-wider">{label}</span>
      </div>
      <div className="relative text-2xl xl:text-3xl font-bold text-white mb-1 font-mono tabular-nums">{value}</div>
      <div className="relative text-[10px] text-gray-500 font-mono">{sub}</div>
      <Icon size={64} className={`absolute -bottom-3 -right-3 opacity-[0.06] ${palette.text}`} />
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, sub, tone, testId }) {
  const toneCls = tone === 'green'      ? 'from-emerald-500/15 text-emerald-400 border-emerald-700/30' :
                  tone === 'amber-deep' ? 'from-orange-600/15 text-orange-400 border-orange-700/40' :
                                          'from-amber-500/15 text-amber-400 border-amber-700/30';
  return (
    <div className={`relative bg-slate-900 border ${toneCls.split(' ').filter(c => c.startsWith('border')).join(' ')} bg-gradient-to-br ${toneCls.split(' ').filter(c => c.startsWith('from')).join(' ')} to-slate-900 rounded-xl p-5 overflow-hidden group hover:scale-[1.02] transition`} data-testid={testId}>
      <div className={`flex items-center gap-2 ${toneCls.split(' ').filter(c => c.startsWith('text')).join(' ')} mb-3`}>
        <Icon size={16} />
        <span className="text-[10px] font-bold uppercase tracking-wider">{label}</span>
      </div>
      <div className="text-3xl font-bold text-white mb-1 font-mono">{value.toLocaleString('tr-TR')}</div>
      <div className="text-[10px] text-gray-500 font-mono">{sub}</div>
      <Icon size={64} className={`absolute -bottom-3 -right-3 opacity-[0.06] ${toneCls.split(' ').filter(c => c.startsWith('text')).join(' ')}`} />
    </div>
  );
}

function Panel({ title, icon: Icon, children, className = '', testId }) {
  return (
    <div className={`bg-slate-900/50 border border-slate-800 rounded-xl p-5 ${className}`} data-testid={testId}>
      <div className="flex items-center gap-2 mb-4">
        {Icon && <Icon size={14} className="text-amber-500" />}
        <h3 className="text-sm font-bold text-white tracking-tight">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function EmptyChart({ message }) {
  return (
    <div className="h-48 flex items-center justify-center text-center text-gray-500 italic text-sm px-6">
      {message}
    </div>
  );
}

const tooltipStyle = {
  backgroundColor: '#0F172A',
  border: '1px solid #334155',
  borderRadius: '8px',
  fontSize: '12px',
  color: '#FFF',
};
